import { useGetPage } from 'hooks/useGetPage';
import {
  GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS,
  pageIds,
  questionnairesIds,
} from 'utilities/constants';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { MyFormData } from 'auth/pages/SignUpProvider/useValidationSteps';
import { DEFAULT_MAX_LENGTH } from 'const/validation';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { GenericStep } from 'components/stepper/types';
import GeneralInfo from './forms/GeneralInfo';
import Medication from '../../../../health-questionnaire/forms/Medication';
import { useQuestionnaireStepperForm } from 'components/stepper/useStepperForm';
import {
  FhirQuestionnaireNames,
  GetQuestionnaireOutput,
  GetFhirQuestionnaireResponseQuery,
  useGetFhirQuestionnaireByIdQuery,
  useGetFhirQuestionnaireResponseQuery,
  useGetMedicationByHealthQuestionnairePatientCodexIdQuery,
  useInsertFhirQuestionnaireResponseMutation,
  useUpdateFhirQuestionnaireResponseMutation,
  GetFhirQuestionnaireResponseDocument,
  GetFhirPatientbyCodexIdDocument,
} from 'graphql/generated/remote-schema-hasura';
import { useAuth } from 'auth/context/AuthContext';
import {
  FileOutput,
  HasuraAnswerItem,
  QuestionnaireResponseStatus,
  useUpdateSelfReportedMedicationMutation,
} from 'graphql/generated/hasura';
import { useCustomToast } from 'layout/useToast';
import { useNavigate } from 'react-router-dom';
import { MY_ACCOUNT_PROFILE } from 'utilities/routes';
import { interpolateVariables } from 'utilities/functions';
import { arrayRequired } from 'utilities/validationSchemas/questionnaireValidation';
import {
  MedAllergens,
  SavedMedicationOutput,
} from '../../../../health-questionnaire/useQuestionaireValidations';
import { Roles } from '../../../../../../../firebase/interfaces';
import { DEFAULT_NONE_OPTION } from 'components/stepper/field-components/FormMultiSelect';
import moment from 'moment/moment';
import { STANDARD_DATE } from 'utilities/dateHelper';
import { isValidNdc } from 'utilities/questionnaireHelpers';
import { emailPattern } from 'utilities/variables';
import { isJsonString } from '../../../../../../my-appointments/components/appointment-details/useSummariForm';

export const MED_ALLERGENS_ID = '101';
export const ADDITIONAL_CHILD_INFORMATION_ID = '183';

export const ADDITIONAL_TEXT_KEY =
  `${ADDITIONAL_CHILD_INFORMATION_ID}_text` as const;
export const ADDITIONAL_FILE_KEY =
  `${ADDITIONAL_CHILD_INFORMATION_ID}_file` as const;
export const ADDITIONAL_FILES_KEY =
  `${ADDITIONAL_CHILD_INFORMATION_ID}_files` as const;

export type AdditionalOutputType = {
  [ADDITIONAL_TEXT_KEY]: string;
  [ADDITIONAL_FILE_KEY]: string;
  [ADDITIONAL_FILES_KEY]: FileOutput[];
};

type ChildHealthKey =
  | '167'
  | '168'
  | '169'
  | '170'
  | '171'
  | '172'
  | '173'
  | '174'
  | '175'
  | '176'
  | '177'
  | '178'
  | '179'
  | '180'
  | '181'
  | '182';

type ChildHealthMap = Record<ChildHealthKey, string | Array<string>>;

const MEDICATION_IDS = {
  name: '75',
  dose: '79',
  time: '81',
  notes: '84',
  medicationList: '99',
};

export type Medication = {
  name: string;
  label?: string;
  startDate?: string;
  endDate?: string;
  dose: string;
  units: string;
  frequency: {
    count: string;
    time: string;
  };
  notes: string;
  isSaved: boolean;
  regularly?: boolean;
};

export type ChildHealthDataType = ChildHealthMap & {
  medication: Medication[];
  medicationOld: Medication[];
  allergens: string[];
  medAllergens: MedAllergens[];
  [MED_ALLERGENS_ID]: string[];
};

let insertionLockForUser: string | null = null;

export const useChildQuestionnaireValidations = () => {
  const { user: loggedUser, selectedAccount } = useAuth();
  const { addToast } = useCustomToast();
  const navigate = useNavigate();
  const { data } = useGetPage({
    locale: 'en',
    pageId: pageIds.MY_PROFILE,
  });

  const [checkedResponse, setCheckedResponse] = useState(false);
  const isFormPopulated = useRef(false);

  const { data: questionneire } = useGetFhirQuestionnaireByIdQuery({
    fetchPolicy: 'network-only',
    variables: {
      questionnaire_id: questionnairesIds.CHILD_HEALTH_QUESTIONNAIRE,
    },
  });

  const {
    data: savedQuestionaire,
    loading: responseLoading,
    error,
    refetch,
  } = useGetFhirQuestionnaireResponseQuery({
    fetchPolicy: 'network-only',
    context: {
      ignoreGlobalErrors: true,
    },
    skip: !selectedAccount || loggedUser?.role !== Roles.PATIENT,
    variables: {
      codexPatientId: selectedAccount || '',
      identifier: FhirQuestionnaireNames.HealthQuestionnaire,
      system: GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS.codex,
    },
    onCompleted: () => {
      setCheckedResponse(true);
    },
    onError: () => {
      setCheckedResponse(true);
    },
  });

  const { data: savedMedications, loading: savedMedicationLoading } =
    useGetMedicationByHealthQuestionnairePatientCodexIdQuery({
      variables: {
        patientCodexId: selectedAccount ?? '',
      },
    });

  const isComplete = useMemo(
    () =>
      savedQuestionaire?.getFHIRQuestionnaireResponse?.questionnaire_response
        ?.is_complete === QuestionnaireResponseStatus.Completed,
    [savedQuestionaire],
  );

  const [
    insertFhirQuestionnaireResponses,
    { loading: isSavingFhirQuestionnaireResponses },
  ] = useInsertFhirQuestionnaireResponseMutation({
    refetchQueries: [
      {
        query: GetFhirQuestionnaireResponseDocument,
        variables: {
          codexPatientId: selectedAccount || '',
          identifier: FhirQuestionnaireNames.HealthQuestionnaire,
          system: GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS.codex,
        },
      },
    ],
  });

  const locale = useMemo(
    () => data.validateErrors || { required: 'Field is required' },
    [data],
  );

  const [updateFhirQuestionnaireResponses, { loading: submitingStep }] =
    useUpdateFhirQuestionnaireResponseMutation({
      refetchQueries: [
        { query: GetFhirPatientbyCodexIdDocument },
        {
          query: GetFhirQuestionnaireResponseDocument,
          variables: {
            codexPatientId: selectedAccount || '',
            identifier: FhirQuestionnaireNames.HealthQuestionnaire,
            system: GOOGLE_EHR_AVAILABLE_SYSTEMS_IDENTIFIERS.codex,
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        console.log('updated');
      },
    });

  const [updateMedications] = useUpdateSelfReportedMedicationMutation({
    refetchQueries: ['GetMedicationByHealthQuestionnairePatientCodexId'],
    onError: (error) => addToast({ message: error?.message, type: 'warning' }),
  });

  const getInitialQuestionerData = useCallback(
    (savedQuestionaire?: GetFhirQuestionnaireResponseQuery) => {
      if (
        !savedQuestionaire ||
        !savedQuestionaire.getFHIRQuestionnaireResponse
      ) {
        return {};
      }

      const data =
        savedQuestionaire.getFHIRQuestionnaireResponse.questionnaire_response
          .responses ?? [];

      const medicationResponse =
        savedQuestionaire.getFHIRQuestionnaireResponse.questionnaire_response.responses.find(
          (questionnaire: Record<string, unknown>) =>
            questionnaire?.questionID?.toString() ===
            MEDICATION_IDS.medicationList,
        )?.hasuraItemArrayResponse ?? [];

      const medicationOld = medicationResponse.map((med: HasuraAnswerItem) => {
        const [name, dose, unit, count, time, notes] =
          med?.hasuraItemArrayResponse ?? [];
        return {
          name: name?.stringResponse ?? '',
          dose: dose?.hasuraMedicationItemResponse?.value,
          units: unit?.hasuraMedicationItemResponse?.value,
          frequency: {
            count: count?.hasuraMedicationItemResponse?.value,
            time: time?.hasuraMedicationItemResponse?.value,
          },
          notes: notes?.stringResponse ?? '',
          isSaved: true,
        };
      });

      const [medAllergens] = data.reduce(
        (acc, questionnaire: Record<string, unknown>) => {
          const questionID = questionnaire?.questionID as string;
          if (questionID.toString() !== MED_ALLERGENS_ID) {
            return acc;
          }

          const formatedResponse = (
            questionnaire.stringArrayResponse as string[]
          )
            ?.map((question) => JSON.parse(question))
            ?.filter(({ value }) => !isValidNdc(value));
          const medAllergens = formatedResponse?.length
            ? formatedResponse.map(({ value, label, reaction }) => ({
                label,
                medication: value,
                reaction,
              }))
            : [
                {
                  label: '',
                  medication: '',
                  reaction: [],
                },
              ];

          return [...acc, medAllergens];
        },
        [] as unknown[][],
      );

      const medication = (
        savedMedications?.getFHIRMedicationRequestByPatientCodexId
          ?.medication_requests ?? []
      ).map((med: SavedMedicationOutput) => {
        const {
          ndc,
          duration,
          frequency,
          note,
          dosage,
          displayName,
          effectiveDate,
          endDate,
        } = med;
        const label = `${displayName} ${dosage}`;
        return {
          label: label,
          name: ndc,
          frequency: {
            count: frequency,
            time: duration,
          },
          startDate: effectiveDate
            ? moment(effectiveDate).format(STANDARD_DATE)
            : undefined,
          endDate: endDate ? moment(endDate).format(STANDARD_DATE) : '',
          regularly: !endDate,
          notes: note,
          isSaved: true,
        };
      });

      const additionalInfoResponse =
        savedQuestionaire.getFHIRQuestionnaireResponse.questionnaire_response.responses.find(
          (questionnaire: Record<string, unknown>) =>
            questionnaire?.questionID?.toString() ===
            ADDITIONAL_CHILD_INFORMATION_ID,
        )?.stringResponse;

      const additionalInfo = isJsonString(additionalInfoResponse ?? '')
        ? JSON.parse(additionalInfoResponse ?? '')
        : undefined;

      return {
        ...data.reduce(
          (
            acc: Record<string, unknown>,
            questionnaire: Record<string, unknown>,
          ) => {
            const questionID = questionnaire?.questionID as string;
            if (questionID.toString() === MEDICATION_IDS.medicationList) {
              return acc;
            }
            if (questionID.toString() === '168') {
              return isValidPhoneNumber(questionnaire.stringResponse as string)
                ? { ...acc, '168': questionnaire.stringResponse }
                : acc;
            }
            return {
              ...acc,
              [questionID]:
                questionnaire.stringResponse ||
                questionnaire.stringArrayResponse,
            };
          },
          {},
        ),
        medAllergens: medAllergens?.length
          ? medAllergens
          : [
              {
                label: '',
                medication: '',
                reaction: [],
              },
            ],
        [ADDITIONAL_TEXT_KEY]: additionalInfo?.detail,
        [ADDITIONAL_FILES_KEY]: additionalInfo?.files,
        medication,
        medicationOld,
      };
    },
    [savedMedications],
  );

  const generalValidation = yup.object<Partial<MyFormData>>().shape({
    '167': yup.string().trim().max(DEFAULT_MAX_LENGTH),
    medAllergens: yup.array().of(
      yup.object({
        medication: yup.string().required(locale?.required),
        label: yup.string().required(locale?.required),
        reaction: yup
          .array()
          .of(yup.string())
          .when('medication', {
            is: (val: string) => val !== DEFAULT_NONE_OPTION,
            then: (schema) =>
              schema.min(1, locale?.required).required(locale?.required),
            otherwise: (schema) => schema.notRequired(),
          }),
      }),
    ),
    '168': yup
      .string()
      .nullable()
      .test(
        'is-valid-phone',
        locale?.phoneValid ?? 'Please enter a valid phone number',
        (value) => {
          if (!value) return true;
          const phone = value?.startsWith('+') ? value : `+${value}`;
          return phone ? isValidPhoneNumber(phone) : false;
        },
      ),
    '160': yup.string().trim().max(DEFAULT_MAX_LENGTH),
    '171': yup
      .string()
      .notRequired()
      .email('Invalid email')
      .matches(new RegExp(emailPattern), 'Invalid email'),
    '172': arrayRequired(locale),
    '173': yup.string().required(locale?.required),
    '174': yup.string().required(locale?.required),
    '175': yup.string().required(locale?.required),
    '176': yup.string().required(locale?.required),
    '177': yup.string().required(locale?.required),
    '178': yup.string().required(locale?.required),
    '179': yup.string().required(locale?.required),
    '180': yup.string().when('179', {
      is: (value: string) => {
        return !value || value === 'Never';
      },
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.required(locale?.required),
    }),
    '181': yup.array().of(yup.string()).min(1, locale?.required),
    '182': yup.array().of(yup.string()).min(1, locale?.required),
  });

  const medication = yup.object<Partial<MyFormData>>().shape({
    medication: yup.array().of(
      yup.object({
        name: yup.string().required(locale?.required),
        startDate: yup
          .date()
          .required(locale?.required)
          .typeError(locale?.required),
        endDate: yup
          .date()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value,
          )
          .nullable()
          .when('regularly', {
            is: false,
            then: (schema) =>
              schema
                .typeError(locale?.required)
                .required(locale?.required)
                .min(
                  yup.ref('startDate'),
                  locale?.invalidDate || 'End date cannot be before start date',
                ),
            otherwise: (schema) => schema.notRequired(),
          }),
        notes: yup.string().nullable(),
        frequency: yup
          .object({
            count: yup.string().required(locale?.required),
            time: yup.string().required(locale?.required),
          })
          .required(locale?.required),
      }),
    ),
  });

  const GeneralInfoComponent = useCallback(
    () =>
      GeneralInfo({
        sectionIndex: 0,
        getQuestionnaireOutput:
          questionneire?.getFHIRQuestionnaireByCodexID as GetQuestionnaireOutput,
      }),
    [questionneire],
  );

  const MedicationComponent = useCallback(
    () =>
      Medication({
        sectionIndex: 1,
        getQuestionnaireOutput:
          questionneire?.getFHIRQuestionnaireByCodexID as GetQuestionnaireOutput,
      }),
    [questionneire],
  );

  const steps: GenericStep[] = [
    {
      id: 'generali-info',
      name:
        questionneire?.getFHIRQuestionnaireByCodexID?.data?.[0].name ??
        'Generali Info',
      schema: generalValidation,
      component: GeneralInfoComponent,
    },
    {
      id: 'medications',
      name:
        questionneire?.getFHIRQuestionnaireByCodexID?.data?.[1].name ??
        'Medications',
      schema: medication,
      component: MedicationComponent,
    },
  ];

  const insertRef = useRef(false);

  const { currentStep, methods, handleNextStep, onError } =
    useQuestionnaireStepperForm<ChildHealthDataType & AdditionalOutputType>({
      steps,
      searchLastSavedStep: false,
      initialValues: {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        '168': undefined,
        '181': ['None'],
        '182': ['None'],
        medication: [],
        medAllergens: [
          {
            label: '',
            medication: '',
            reaction: [],
          },
        ],
      },
    });

  useEffect(() => {
    if (
      !isFormPopulated.current &&
      !savedMedicationLoading &&
      savedQuestionaire?.getFHIRQuestionnaireResponse
    ) {
      const fetchedData = getInitialQuestionerData(savedQuestionaire);
      const finalInitialValues = {
        '168': undefined,
        '181': ['None'],
        '182': ['None'],
        medication: [],
        medAllergens: [
          {
            label: '',
            medication: '',
            reaction: [],
          },
        ],
        ...fetchedData,
      };

      methods.reset(finalInitialValues);
      isFormPopulated.current = true;
    }
  }, [
    savedQuestionaire,
    getInitialQuestionerData,
    methods,
    savedMedicationLoading,
  ]);

  const saveData = async (
    data: ChildHealthDataType & AdditionalOutputType,
    isLastStep = true,
  ) => {
    const {
      medication,
      medicationOld,
      [ADDITIONAL_TEXT_KEY]: additionalText,
      [ADDITIONAL_FILES_KEY]: additionalFiles,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      [ADDITIONAL_FILE_KEY]: remove,
      medAllergens = [
        {
          label: '',
          medication: '',
          reaction: [],
        },
      ],
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      [MED_ALLERGENS_ID]: allergensPressure,
      ...rest
    } = data;

    const medicationRequest = (medicationOld ?? []).map(
      ({ name, dose, isSaved, notes, frequency, units }, index) => ({
        completed: isSaved,
        id: index,
        title: `Medication list ${index + 1}`,
        answers: [
          {
            questionID: MEDICATION_IDS.name,
            response: name,
          },
          {
            questionID: MEDICATION_IDS.dose,
            response: { id: 'answer-1', value: dose ? dose.toString() : '' },
          },
          {
            questionID: MEDICATION_IDS.dose,
            response: { id: 'answer-2', value: units ?? '' },
          },
          {
            questionID: MEDICATION_IDS.time,
            response: { id: 'answer-1', value: frequency.count ?? '' },
          },
          {
            questionID: MEDICATION_IDS.time,
            response: { id: 'answer-2', value: frequency.time ?? '' },
          },
          {
            questionID: MEDICATION_IDS.notes,
            response: notes,
          },
        ],
      }),
    );

    const allergensValue = medAllergens.map(({ label, reaction, medication }) =>
      JSON.stringify({ label: label, value: medication, reaction }),
    );

    const allergensPressureRequest = {
      questionID: MED_ALLERGENS_ID,
      currentSection: 'Health History',
      response: allergensValue,
    };

    const request = Object.entries(rest)
      .filter(
        ([key, value]) => !!value && key !== ADDITIONAL_CHILD_INFORMATION_ID,
      )
      .map(([key, value]) => ({
        questionID: key,
        response: value,
        currentSection:
          questionneire?.getFHIRQuestionnaireByCodexID?.data?.find(
            ({ properties }) => properties.includes(key),
          )?.name,
      }));

    const additionalRequest = {
      questionID: ADDITIONAL_CHILD_INFORMATION_ID,
      currentSection: 'Health History',
      response: JSON.stringify({
        detail: additionalText,
        files: additionalFiles,
      }),
    };

    if (isLastStep) {
      const medRequest = medication.map(
        ({
          name,
          notes,
          frequency,
          startDate,
          endDate,
          regularly = false,
        }) => ({
          ndc: name,
          note: notes,
          duration: frequency?.time,
          frequency: frequency.count,
          startDate: moment(startDate).format(STANDARD_DATE),
          endDate: !regularly
            ? moment(endDate).format(STANDARD_DATE)
            : undefined,
        }),
      );
      const { data } = await updateMedications({
        variables: {
          medications: medRequest,
        },
      });

      if (!data) {
        throw new Error('Medications not added');
      }
    }

    return updateFhirQuestionnaireResponses({
      variables: {
        questionnaireId: '',
        identifier: FhirQuestionnaireNames.HealthQuestionnaire,
        responses: [
          allergensPressureRequest,
          additionalRequest,
          ...request,
          {
            currentSection: 'Medications',
            questionID: MEDICATION_IDS.medicationList,
            response: medicationRequest,
          },
        ],
        swabCode: '',
        isComplete: isComplete || isLastStep,
      },
      onCompleted: () => {
        addToast({
          type: 'success',
          message: interpolateVariables(
            'Step {{step}}/{{stepsTotal}} of health questionnaire is updated successfully',
            {
              step: (currentStep + 1).toString(),
              stepsTotal: steps.length.toString(),
            },
          ),
        });

        if (!isLastStep) return;

        navigate(`${MY_ACCOUNT_PROFILE}/profile`);
      },
    });
  };

  const handleSaveStep = async (
    data: ChildHealthDataType & AdditionalOutputType,
    currentStep: number,
  ) => {
    return methods.handleSubmit(
      () =>
        saveData(data, currentStep >= steps.length - 2)
          .then(() => (isFormPopulated.current = false))
          .then(() => handleNextStep())
          .catch(console.warn),
      onError,
    )();
  };

  const onSubmit = (data: ChildHealthDataType & AdditionalOutputType) => {
    saveData(data, true);
  };

  useEffect(() => {
    if (
      checkedResponse &&
      !responseLoading &&
      !savedQuestionaire?.getFHIRQuestionnaireResponse.questionnaire_response
        .id &&
      selectedAccount &&
      insertionLockForUser !== selectedAccount &&
      !insertRef.current
    ) {
      insertRef.current = true;
      insertionLockForUser = selectedAccount;

      insertFhirQuestionnaireResponses({
        variables: {
          createQuestionnaireResponsesInput: {
            codexPatientId: selectedAccount,
            questionnaireName: FhirQuestionnaireNames.HealthQuestionnaire,
            questionnaireResponse: [],
            serviceRequestId: '',
            completed: false,
          },
        },
        onCompleted: () => {
          refetch();
        },
      });
    }
  }, [
    checkedResponse,
    insertFhirQuestionnaireResponses,
    refetch,
    error,
    responseLoading,
    savedQuestionaire?.getFHIRQuestionnaireResponse.questionnaire_response.id,
    selectedAccount,
  ]);

  return {
    currentStep,
    methods,
    handleNextStep,
    steps,
    onSubmit,
    onError,
    handleSaveStep,
    submitingStep,
    submited: isSavingFhirQuestionnaireResponses,
    totalSteps: steps.length,
  };
};
